/**
 * Formatting constants shared by every surface. The board, the analytics screen and
 * the Excel export must agree, so the rules live here and `lib/format.ts` on each
 * side consumes them. Never format inline.
 */

/** Version 1.0 is single-currency; multi-currency reporting is out of scope. */
export const CURRENCY = "CZK" as const;
export const CURRENCY_DECIMALS = 2;

/**
 * The locale the board **sorts** in — fixed for the application, never the reader's.
 *
 * This is the half of the retired `LOCALE` constant that must not follow a user preference.
 * Within-cell order is derived, and its final tie-break is a name comparison: two people
 * looking at one board in one meeting have to see one order, so collation is shared state
 * even though display formatting is personal. A reader who switched to English and thereby
 * re-sorted their colleague's cards would reintroduce the very bug `sales-board.md` fixed.
 *
 * Czech, because the data is: `Č` sorts after `C` here and between `C` and `D` in `en`.
 *
 * Display formatting — currency, dates, numbers — is the other half, and it lives in
 * `apps/web/src/lib/format.ts` reading the session user's `locale`.
 */
export const COLLATION_LOCALE = "cs" as const;

/**
 * The application's civil timezone. Quarter boundaries and "whole days elapsed" are
 * Czech calendar facts, not UTC ones — evaluating a day count in UTC would flip an
 * opportunity's stalled state at 01:00 local in winter and 02:00 in summer.
 *
 * The API reads the runtime value from `APP_TIMEZONE`; this is the expected default
 * and the single place the name is written down.
 */
export const APP_TIMEZONE_ENV = "APP_TIMEZONE" as const;
export const DEFAULT_APP_TIMEZONE = "Europe/Prague" as const;

/** Business dates are calendar dates — no time, no offset, no quarter ambiguity. */
export const DATE_FORMAT = "yyyy-MM-dd" as const;
